import { create } from 'zustand';
import vendorService from '../services/vendorService';
import useNotificationStore from './notificationStore';

const useVendorStore = create((set, get) => ({
  // State
  vendors: [],
  currentVendor: null,
  isLoading: false,
  error: null,
  searchResults: [],
  totalCount: 0,
  meta: {
    totalItems: 0,
    totalPages: 1,
    currentPage: 1,
    pageSize: 20
  },

  // Actions
  setLoading: (isLoading) => set({ isLoading }),
  setError: (error) => set({ error }),
  setVendors: (vendors) => set({ vendors }),
  setCurrentVendor: (vendor) => set({ currentVendor: vendor }),
  setSearchResults: (results) => set({ searchResults: results }),
  setMeta: (meta) => set({ meta }),

  // Fetch vendors
  fetchVendors: async (params = {}) => {
    get().setLoading(true);
    get().setError(null);
    
    try {
      const { vendors, meta } = await vendorService.getVendors(params);
      console.log('[VendorStore] fetchVendors received:', { vendors, meta });
      
      set({
        vendors,
        meta,
        totalCount: meta?.totalItems || 0,
        isLoading: false
      });
      
      return { data: vendors, meta };
    } catch (error) {
      set({
        error: error.message,
        isLoading: false
      });
      console.error('[VendorStore] fetchVendors error:', error);
      // Return empty data instead of throwing
      return { data: [], meta: { totalItems: 0, totalPages: 1, currentPage: 1, pageSize: 20 } };
    }
  },

  // Fetch vendor by ID
  fetchVendorById: async (id) => {
    get().setLoading(true);
    get().setError(null);
    
    try {
      const vendor = await vendorService.getVendorById(id);
      set({
        currentVendor: vendor,
        isLoading: false
      });
      return vendor;
    } catch (error) {
      set({
        error: error.message,
        isLoading: false
      });
      
      // Show error notification
      const { showError } = useNotificationStore.getState();
      showError('Failed to fetch vendor details');
      
      // Don't throw error - return null
      return null;
    }
  },

  // Create vendor
  createVendor: async (vendorData) => {
    get().setLoading(true);
    get().setError(null);
    
    try {
      const vendor = await vendorService.createVendor(vendorData);
      
      // Add to vendors list
      set(state => ({
        vendors: [vendor, ...state.vendors],
        isLoading: false
      }));
      
      return vendor;
    } catch (error) {
      set({
        error: error.message,
        isLoading: false
      });
      
      // Show error notification
      const { showError } = useNotificationStore.getState();
      showError('Failed to create vendor');
      
      throw error; // Keep throwing for create operations
    }
  },

  // Quick create vendor
  quickCreateVendor: async (vendorData) => {
    get().setError(null);
    
    try {
      const vendor = await vendorService.quickCreateVendor(vendorData);
      
      // Add to vendors list
      set(state => ({
        vendors: [vendor, ...state.vendors]
      }));
      
      return vendor;
    } catch (error) {
      set({ error: error.message });
      throw error;
    }
  },

  // Update vendor
  updateVendor: async (id, vendorData) => {
    get().setLoading(true);
    get().setError(null);
    
    try {
      const updatedVendor = await vendorService.updateVendor(id, vendorData);
      
      // Update in list
      set(state => ({
        vendors: state.vendors.map(v => v._id === id ? updatedVendor : v),
        currentVendor: state.currentVendor?._id === id ? updatedVendor : state.currentVendor,
        isLoading: false
      }));
      
      return updatedVendor;
    } catch (error) {
      set({
        error: error.message,
        isLoading: false
      });
      
      // Show error notification
      const { showError } = useNotificationStore.getState();
      showError('Failed to update vendor');
      
      throw error; // Keep throwing for update operations
    }
  },

  // Delete vendor
  deleteVendor: async (id) => {
    get().setLoading(true);
    get().setError(null);
    
    try {
      await vendorService.deleteVendor(id);
      
      // Remove from list
      set(state => ({
        vendors: state.vendors.filter(v => v._id !== id),
        currentVendor: state.currentVendor?._id === id ? null : state.currentVendor,
        isLoading: false
      }));
      
      return true;
    } catch (error) {
      set({
        error: error.message,
        isLoading: false
      });
      
      // Show error notification
      const { showError } = useNotificationStore.getState();
      showError('Failed to delete vendor');
      
      throw error; // Keep throwing for delete operations
    }
  },

  // Search vendors
  searchVendors: async (query, options = {}) => {
    console.log('Store searchVendors called with:', { query, options });
    
    // Allow empty query for searching
    if (query === null || query === undefined) {
      console.log('Null/undefined query, returning empty array');
      set({ searchResults: [] });
      return [];
    }
    
    get().setError(null);
    
    try {
      console.log('Calling vendorService.searchVendors with query:', query);
      const results = await vendorService.searchVendors(query, options);
      console.log('Store received results:', results);
      console.log('Results type:', typeof results);
      console.log('Is array:', Array.isArray(results));
      console.log('Results length:', results?.length);
      
      set({ searchResults: results || [] });
      return results || [];
    } catch (error) {
      console.error('Store searchVendors error:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response,
        stack: error.stack
      });
      set({ error: error.message });
      // Always return array, never throw
      return [];
    }
  },

  // Get top vendors
  fetchTopVendors: async (limit = 10) => {
    get().setError(null);
    
    try {
      const vendors = await vendorService.getTopVendors(limit);
      return vendors;
    } catch (error) {
      set({ error: error.message });
      throw error;
    }
  },

  // Check vendor name
  checkVendorName: async (name) => {
    try {
      const result = await vendorService.checkVendorName(name);
      console.log('Store checkVendorName result:', result);
      return result || { exists: false, vendor: null };
    } catch (error) {
      console.error('Error checking vendor name:', error);
      return { exists: false, vendor: null };
    }
  },

  // Clear store
  clearStore: () => set({
    vendors: [],
    currentVendor: null,
    isLoading: false,
    error: null,
    searchResults: [],
    meta: {
      totalItems: 0,
      totalPages: 1,
      currentPage: 1,
      pageSize: 20
    }
  })
}));

export default useVendorStore;